import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { saveAs } from 'file-saver';

import AlphabetFilter from "./components/AlphabetFilter";
import Icon from '@components/custom/Icon';
import Button from '@components/custom/Button';
import ModuleHeader from '@components/ModuleHeader';
import ScrollList from '@components/custom/ScrollList';
import Spinner from '@components/custom/Spinner';
import Table from '@components/custom/Table';

import '@assets/css/bankCodes.css';

import { useClipboard } from '@sm/hooks/useClipboard';
import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';

const BankCodes = () => {
  const dispatch = useDispatch();
  const copyToClipboard = useClipboard();

  const [isLoading, setIsLoading] = useState(false);
  const [bankCodes, setBankCodes] = useState([]);
  const [allBankCodes, setAllBankCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');

  const columns = [{
    value: "code",
    label: "Bank Code",
    leftAligned: true,
    width: "20%"
  }, {
    value: "name",
    label: "Bank",
    leftAligned: true
  }];

  const prepareBankCodesData = useMemo(() => {
    return bankCodes.map((bankCode) => ({
      ...bankCode,
      code: (
        <div className='d-flex align-items-center'>
          <Icon name='content_copy' size={ 20 } style={ { cursor: 'pointer' } } onClick={ () => copyToClipboard(bankCode.code) } />
          { bankCode.code }
        </div>
      ),
    }));
  }, [bankCodes, copyToClipboard]);

  const handleExportClick = () => {
    crudActions.post(`v1/banks/merchant/search/xlsx?currency=${ selectedCurrency }`).then(
      response => {
        if (response) {
          response.blob().then(result => {
            saveAs(result, `Bank Codes - ${ selectedCurrency }.xlsx`);
          });
        }
      }
    ).catch(err => {
      dispatch(showAlert('danger', err?.message));
    });
  };

  const onSelectLetter = (letter) => {
    if (letter === 'All') {
      setBankCodes(allBankCodes);
      return;
    }

    const filtered = allBankCodes.filter(b =>
      b.name && b.name.trim().toUpperCase().startsWith(letter.toUpperCase())
    );
    setBankCodes(filtered);
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    crudActions.get('v1/clients/lookups/bank-codes')
      .then(response => {
        if (isMounted && response) {
          setCurrencies(response.currencies ?? []);
          setSelectedCurrency(response.currencies?.[0]?.value ?? '');
        }
      })
      .catch(err => {
        if (isMounted) {
          dispatch(showAlert('danger', err?.message));
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!selectedCurrency) return;

    setIsLoading(true);
    crudActions.post(`v1/banks/merchant/search?currency=${ selectedCurrency }`)
      .then(response => {
        setBankCodes(response);
        setAllBankCodes(response);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => setIsLoading(false));
  }, [selectedCurrency, dispatch]);

  return (
    <div className='col-12 module finances bankCodes'>
      <div className='component shadow mb-4 position-relative'>
        <ModuleHeader
          id='encrypted'
          title='Bank Codes'
          actions={
            <>
              <ScrollList
                items={ currencies }
                selectedItem={ selectedCurrency }
                onClick={ (value) => setSelectedCurrency(value) }
              />
              <Button
                label='Export'
                icon={ true }
                iconName='table_convert'
                onClick={ handleExportClick }
              />
            </>
          }
        />

        { isLoading && <Spinner/> }

        <AlphabetFilter
          key={ selectedCurrency }
          onSelectLetter={ onSelectLetter }
        />
        
        <Table
          columns={ columns }
          data={ prepareBankCodesData }
        />
      </div>
    </div>
  );
};

export default BankCodes;